import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, retry, timeout } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface LoginForm {
    phone?: string;
    password?: string;
}

@Injectable({
    providedIn: 'root',
})
export class ApiService {
    private readonly DEFAULT_TIMEOUT = 65000;
    private readonly MAX_RETRIES = 3;
    private readonly RETRY_DELAY = 1000;
    
    constructor(
        private http: HttpClient,
        private injector: Injector
    ) { }
    
    private handleError = (error: HttpErrorResponse): Observable<never> => {
        console.log('[ApiService] handleError called:', error.status, error.url);
        
        if (error.status === 401) {
            console.log('[ApiService] 401 detected, triggering logout');
            import('../services/auth.service').then(m => {
                const authService = this.injector.get(m.AuthService);
                authService.logOutUser();
            });
        }
        
        let errorMessage = 'An unknown error occurred';
        
        if (error.error instanceof ErrorEvent) {
            errorMessage = `Network error: ${error.error.message}`;
        } else {
            errorMessage = `Server error: ${error.status} - ${error.message}`;
            
            if (error.status === 0) {
                errorMessage = 'No internet connection or server is unreachable';
            } else if (error.status === 404) {
                errorMessage = 'Resource not found';
            } else if (error.status === 500) {
                errorMessage = 'Internal server error';
            }
        }
        
        console.error('API Error:', errorMessage, error);
        return throwError(() => error);
    }
    
    /**
     * Retry strategy with exponential backoff
     */
    private retryStrategy = ({
        maxRetryAttempts = this.MAX_RETRIES,
        excludedStatusCodes = [400, 401, 403, 404, 422]
    }: {
        maxRetryAttempts?: number;
        excludedStatusCodes?: number[];
    } = {}) => {
        return (attempts: Observable<any>) => {
            return attempts.pipe(
                retry({
                    count: maxRetryAttempts,
                    delay: (error: HttpErrorResponse, retryCount) => {
                        // Don't retry on certain status codes
                        if (excludedStatusCodes.includes(error.status)) {
                            return throwError(() => error);
                        }
                        
                        // Exponential backoff: 1s, 2s, 4s
                        const delayMs = this.RETRY_DELAY * Math.pow(2, retryCount - 1);
                        console.log(`Retrying request (attempt ${retryCount}/${maxRetryAttempts}) after ${delayMs}ms...`);
                        return timer(delayMs);
                    }
                })
            );
        };
    };
    get(endPoint: string, options?: { timeout?: number; retry?: boolean }): Observable<any> {
        const timeoutMs = options?.timeout ?? this.DEFAULT_TIMEOUT;
        const shouldRetry = options?.retry ?? true;
        
        let request$ = this.http.get(environment.API_URL + endPoint).pipe(
            timeout(timeoutMs),
            catchError(this.handleError)
        );
        
        if (shouldRetry) {
            request$ = request$.pipe(this.retryStrategy());
        }
        
        return request$;
    }

    post(endPoint: string, body: any, options?: { timeout?: number; retry?: boolean }): Observable<any> {
        const timeoutMs = options?.timeout ?? this.DEFAULT_TIMEOUT;
        const shouldRetry = options?.retry ?? false; // Don't retry POST by default (not idempotent)
        
        let request$ = this.http.post(environment.API_URL + endPoint, body).pipe(
            timeout(timeoutMs),
            catchError(this.handleError)
        );
        
        if (shouldRetry) {
            request$ = request$.pipe(this.retryStrategy());
        }
        
        return request$;
    }

    put(endPoint: string, body: any, options?: { timeout?: number; retry?: boolean }): Observable<any> {
        const timeoutMs = options?.timeout ?? this.DEFAULT_TIMEOUT;
        const shouldRetry = options?.retry ?? false; // Don't retry PUT by default
        
        let request$ = this.http.put(environment.API_URL + endPoint, body).pipe(
            timeout(timeoutMs),
            catchError(this.handleError)
        );
        
        if (shouldRetry) {
            request$ = request$.pipe(this.retryStrategy());
        }
        
        return request$;
    }

    delete(endPoint: string, options?: { timeout?: number; retry?: boolean }): Observable<any> {
        const timeoutMs = options?.timeout ?? this.DEFAULT_TIMEOUT;
        const shouldRetry = options?.retry ?? false; // Don't retry DELETE by default
        
        let request$ = this.http.delete(environment.API_URL + endPoint).pipe(
            timeout(timeoutMs),
            catchError(this.handleError)
        );
        
        if (shouldRetry) {
            request$ = request$.pipe(this.retryStrategy());
        }
        
        return request$;
    }
}